import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Building2, Users, Globe, Award, TrendingUp, Shield } from 'lucide-react';

const AltioraAbout = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-50 to-teal-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About Altiora</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your trusted partner for end-to-end accounting solutions
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        {/* Company Story */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="border-2">
            <CardContent className="p-8 md:p-12 space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="w-10 h-10 text-teal-600" />
                <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
              </div>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Altiora Business Solutions was founded with a clear mission: to bridge the gap between US small-cap companies and world-class accounting services. We understand the challenges businesses face in managing their financial operations efficiently while maintaining focus on growth.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                With 17 years of combined experience working with Fortune 500 companies and small businesses across India and the United States, we bring proven expertise and innovative solutions to help your business thrive.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Based in India with a deep understanding of US business practices, we offer the perfect combination of competitive pricing, exceptional quality, and round-the-clock availability.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Founder/Director Section */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Leadership</h2>
          
          <Card className="border-2 border-teal-200 bg-gradient-to-br from-white to-teal-50">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 bg-gradient-to-br from-teal-600 to-blue-900 rounded-full flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">AP</span>
                  </div>
                </div>
                
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      Aruna Punjabi
                    </h3>
                    <p className="text-lg text-teal-600 font-semibold">Founder & Director</p>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-3 text-gray-700 leading-relaxed">
                    <p>
                      Aruna Punjabi is a seasoned accounting professional with <strong>17 years of comprehensive experience</strong> in accounting and finance across multinational corporations and diverse business environments.
                    </p>
                    
                    <p>
                      Her career spans work with <strong>leading MNCs in India</strong> from 2008 to 2018, followed by extensive experience in the <strong>United States through 2025</strong>, where she gained deep insights into US business practices and regulatory requirements.
                    </p>
                    
                    <p>
                      Currently based in India, Aruna founded Altiora Business Solutions to provide best-in-class accounting services to US companies, helping them close operational gaps and optimize their financial processes with the perfect blend of expertise, efficiency, and cost-effectiveness.
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 pt-4">
                    <Badge className="bg-teal-100 text-teal-700 border-none">17+ Years Experience</Badge>
                    <Badge className="bg-blue-100 text-blue-700 border-none">MNC Background</Badge>
                    <Badge className="bg-purple-100 text-purple-700 border-none">US Market Expert</Badge>
                    <Badge className="bg-orange-100 text-orange-700 border-none">Finance Professional</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Core Values */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Core Values</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 hover:border-teal-500 transition-all">
              <CardContent className="p-6 space-y-4 text-center">
                <Shield className="w-12 h-12 text-teal-600 mx-auto" />
                <h3 className="text-xl font-bold text-gray-900">Integrity</h3>
                <p className="text-gray-600">
                  We operate with the highest ethical standards and complete transparency.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:border-teal-500 transition-all">
              <CardContent className="p-6 space-y-4 text-center">
                <TrendingUp className="w-12 h-12 text-teal-600 mx-auto" />
                <h3 className="text-xl font-bold text-gray-900">Excellence</h3>
                <p className="text-gray-600">
                  We are committed to delivering exceptional results that exceed expectations.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:border-teal-500 transition-all">
              <CardContent className="p-6 space-y-4 text-center">
                <Users className="w-12 h-12 text-teal-600 mx-auto" />
                <h3 className="text-xl font-bold text-gray-900">Partnership</h3>
                <p className="text-gray-600">
                  We view our clients as partners and invest in their long-term success.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Why Choose Altiora */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-teal-600 to-blue-900 text-white border-none">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-3xl font-bold mb-8 text-center">The Altiora Advantage</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Globe className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-1">24/7 Global Support</h3>
                    <p className="text-white/90 text-sm">Round-the-clock availability ensuring your business never stops</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-1">Proven Expertise</h3>
                    <p className="text-white/90 text-sm">17 years of experience across multiple industries and markets</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-1">Best Results</h3>
                    <p className="text-white/90 text-sm">Maximum recoveries within 30 days with client-appreciated outcomes</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-1">Competitive Pricing</h3>
                    <p className="text-white/90 text-sm">Cost-effective solutions without compromising on quality</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AltioraAbout;
